import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, Plus, Eye, Sparkles } from "lucide-react";

const CASE_TYPES = ["All", "Appeal", "Revision", "Review", "Miscellaneous", "Other"];
const STATUSES = [
  "All", "Drafting Pending", "Ready for Drafting", "In Review",
  "Pending Approval", "Finalized", "Returned for Correction", "Withdrawn",
];

export default function Cases() {
  const [cases, setCases] = useState([]);
  const [search, setSearch] = useState("");
  const [caseType, setCaseType] = useState("All");
  const [status, setStatus] = useState("All");
  const [district, setDistrict] = useState("All");
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    const params = {};
    if (caseType !== "All") params.case_type = caseType;
    if (status !== "All") params.case_status = status;
    if (district !== "All") params.district = district;
    if (search) params.search = search;
    api.get("/cases", { params })
      .then((r) => setCases(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [caseType, status, district]);

  const districts = useMemo(() => ["All", ...Array.from(new Set(cases.map((c) => c.district).filter(Boolean)))], [cases]);

  return (
    <Layout breadcrumbs={["SSRD", "Cases"]}>
      <div className="flex items-start justify-between mb-6">
        <div className="space-y-1">
          <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Case Management</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="cases-page-title">Cases</h1>
          <p className="text-sm text-muted-foreground">All SSRD appeals, revisions and miscellaneous matters.</p>
        </div>
        <Link to="/cases/new" data-testid="new-case-button">
          <Button className="gap-2"><Plus className="size-4" /> New Case</Button>
        </Link>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="relative md:col-span-2">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by case number / party / survey…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
                className="pl-9"
                data-testid="cases-search-input"
              />
            </div>
            <Select value={caseType} onValueChange={setCaseType}>
              <SelectTrigger data-testid="filter-case-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                {CASE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={district} onValueChange={setDistrict}>
              <SelectTrigger data-testid="filter-district"><SelectValue placeholder="District" /></SelectTrigger>
              <SelectContent>
                {districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="cases-table">
            <TableHeader>
              <TableRow>
                <TableHead>Case No / Year</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>District / Taluka / Village</TableHead>
                <TableHead>Applicant</TableHead>
                <TableHead>Respondent</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && cases.length === 0 && (
                <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No cases match the filter.</TableCell></TableRow>
              )}
              {cases.map((c) => (
                <TableRow key={c.case_id} data-testid={`case-row-${c.case_id}`}>
                  <TableCell className="font-mono text-xs">{c.case_number}/{c.case_year}</TableCell>
                  <TableCell>{c.case_type}</TableCell>
                  <TableCell className="text-xs">{c.district} · {c.taluka} · {c.village}</TableCell>
                  <TableCell>{c.applicant || "—"}</TableCell>
                  <TableCell>{c.respondent || "—"}</TableCell>
                  <TableCell><StatusBadge status={c.case_status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(c.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/cases/${c.case_id}`}>
                        <Button variant="outline" size="sm" className="gap-1"><Eye className="size-3.5" /> View</Button>
                      </Link>
                      <Link to={`/cases/${c.case_id}/draft`}>
                        <Button size="sm" className="gap-1"><Sparkles className="size-3.5" /> Generate</Button>
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Layout>
  );
}
